import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Link,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Search as SearchIcon,
  Security as PermissionIcon,
  School as CourseIcon,
  ViewModule as ModuleIcon,
} from '@mui/icons-material';
import api from '../services/api';

interface Module {
  id: string;
  title: string;
  description: string | null;
  sortOrder: number;
  _count?: {
    permissions: number;
  };
}

interface Course {
  id: string;
  title: string;
  modules: Module[];
}

export default function AdminModulesPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch all courses with their modules
  const { data: coursesData, isLoading } = useQuery({
    queryKey: ['admin', 'courses-with-modules'],
    queryFn: async () => {
      const response = await api.get('/admin/courses/with-modules');
      return response.data as Course[];
    },
  });

  const courses = coursesData || [];

  // Filter modules based on search
  const filteredCourses = courses.map(course => ({
    ...course,
    modules: course.modules.filter(module =>
      module.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.title.toLowerCase().includes(searchTerm.toLowerCase())
    ),
  })).filter(course => course.modules.length > 0);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          underline="hover"
          color="inherit"
          onClick={() => navigate('/admin')}
        >
          Admin
        </Link>
        <Typography color="text.primary">Module</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4">Modul-Verwaltung</Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Berechtigungen pro Modul verwalten
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={() => navigate('/admin')}
        >
          Zurueck
        </Button>
      </Box>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Module oder Kurse suchen..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          size="small"
        />
      </Paper>

      {/* Courses and Modules */}
      {filteredCourses.length === 0 ? (
        <Alert severity="info">
          {searchTerm ? 'Keine Module gefunden' : 'Keine Kurse/Module vorhanden'}
        </Alert>
      ) : (
        filteredCourses.map((course) => (
          <Paper key={course.id} sx={{ mb: 3 }}>
            {/* Course Header */}
            <Box
              sx={{
                p: 2,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <CourseIcon />
              <Typography variant="h6">{course.title}</Typography>
              <Chip
                label={`${course.modules.length} Module`}
                size="small"
                sx={{ ml: 'auto', bgcolor: 'rgba(255,255,255,0.2)', color: 'inherit' }}
              />
            </Box>

            {/* Modules Table */}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Modul</TableCell>
                    <TableCell>Beschreibung</TableCell>
                    <TableCell align="center">Berechtigungen</TableCell>
                    <TableCell align="right">Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {course.modules.map((module) => (
                    <TableRow key={module.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ModuleIcon color="action" fontSize="small" />
                          <Typography variant="body2" fontWeight={500}>
                            {module.title}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            maxWidth: 300,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {module.description || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={module._count?.permissions || 0}
                          size="small"
                          color={module._count?.permissions ? 'primary' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Berechtigungen verwalten">
                          <IconButton
                            color="primary"
                            onClick={() => navigate(`/admin/modules/${module.id}/permissions`)}
                          >
                            <PermissionIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ))
      )}

      {/* Info Box */}
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          Klicken Sie auf das Berechtigungs-Symbol, um festzulegen, welche Benutzer Zugang zu einem Modul haben.
          Jeder Benutzer kann entweder <strong>Admin</strong> (verwalten) oder <strong>Benutzer</strong> (nutzen) Rechte erhalten.
        </Typography>
      </Alert>
    </Box>
  );
}
